import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AxiosUrl from "../../../AxiosUrl";
import styles from "./AddDomain.module.css";

function AddDomainModal({ setAddDomainModal, setRefresh, Refresh }) {
  const [domain, setDomain] = useState("");
  const [dnsType, setDnsType] = useState("Ecomtech DNS");
  const [loading, setLoading] = useState(false);
  // Nameservers come from /get-dns-config (DefaultDNSConfig.nameservers); never build them from rootDomain
  const [nameservers, setNameservers] = useState(["ns1.custom-dns.com", "ns2.custom-dns.com"]);
  const [primaryIP, setPrimaryIP] = useState("202.5.53.41");
  const [domainError, setDomainError] = useState("");

  const uid = localStorage.getItem("id");

  // Fetch DNS config for nameservers and primaryIP
  useEffect(() => {
    AxiosUrl.get('/get-dns-config')
      .then((response) => {
        if (response.data?.success && response.data?.data) {
          const hosts = (response.data.data.nameservers || [])
            .map((ns) => ns.hostname)
            .filter(Boolean);
          if (hosts.length > 0) {
            setNameservers(hosts);
          }
          if (response.data.data.primaryIP) {
            setPrimaryIP(response.data.data.primaryIP);
          }
        }
      })
      .catch((error) => {
        console.log('Failed to fetch DNS config:', error);
      });
  }, []);

  const fromData = {
    uid,
    sub: "false",
    dnsType,
    domain: domain
      .replace(/^(https?:\/\/)?(www\.)?/, "")
      .replace(/\/+$/, "")
      .toLowerCase(),
  };

  function isValidDomain(domain) {
    if (!domain) return true;
    var domainRegex =
      /^([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    return domainRegex.test(domain);
  }

  const handleDomainChange = (e) => {
    const value = e.target.value;
    setDomain(value);
    if (value && !isValidDomain(value)) {
      setDomainError("Please enter a valid domain (e.g., example.com)");
    } else {
      setDomainError("");
    }
  };

  const submitDomain = () => {
    if (!domain) {
      setDomainError("Domain name is required");
      return;
    }
    if (!isValidDomain(domain)) {
      setDomainError("Please enter a valid domain (e.g., example.com)");
      return;
    }

    setLoading(true);
    AxiosUrl.post(`/add-domain-new/${uid}`, fromData)
      .then((res) => {
        toast.success(res.data);
        setRefresh(Refresh + 1);
        setLoading(false);
        setAddDomainModal(false);
      })
      .catch((error) => {
        toast.error(error.response?.data || "Failed to add domain");
        setLoading(false);
      });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setAddDomainModal(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
            </svg>
            Add Custom Domain
          </h2>
          <button className={styles.closeButton} onClick={() => setAddDomainModal(false)}>
            &times;
          </button>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {/* Info Card */}
          <div className={styles.infoCard}>
            <h4>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
              DNS Configuration Guide
            </h4>
            <p>
              <strong>EcomTech DNS (Recommended):</strong> Set nameservers to{" "}
              {nameservers.map((ns, index) => (
                <React.Fragment key={ns}>
                  {index > 0 ? " and " : ""}
                  <span className={styles.highlight}>{ns}</span>
                </React.Fragment>
              ))}{" "}
              at your domain registrar.
            </p>
            <p style={{ marginTop: "8px" }}>
              <strong>Own DNS:</strong> Add an A record pointing to{" "}
              <span className={styles.highlight}>{primaryIP}</span>
            </p>
          </div>

          {/* Domain Input */}
          <div className={styles.formGroup}>
            <label>Domain Name</label>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                value={domain}
                onChange={handleDomainChange}
                placeholder="example.com or shop.example.com"
                className={domainError ? styles.hasError : ""}
              />
            </div>
            {domainError && (
              <div className={styles.errorMessage}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#dc2626">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                <span>{domainError}</span>
              </div>
            )}
            <p className={styles.hint}>
              Enter your domain without http:// or www. prefix
            </p>
          </div>

          {/* DNS Type Selection */}
          <div className={styles.formGroup}>
            <label>DNS Configuration Type</label>
            <div className={styles.dnsTypeCards}>
              <div
                className={`${styles.dnsTypeCard} ${dnsType === "Ecomtech DNS" ? styles.selected : ""}`}
                onClick={() => setDnsType("Ecomtech DNS")}
              >
                <h5>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill={dnsType === "Ecomtech DNS" ? "#008060" : "#6b7280"}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  EcomTech DNS
                  <span className={styles.badge}>Recommended</span>
                </h5>
                <p>We manage your DNS with 99% uptime guarantee. Just update nameservers at your registrar.</p>
              </div>
              <div
                className={`${styles.dnsTypeCard} ${dnsType === "Own DNS" ? styles.selected : ""}`}
                onClick={() => setDnsType("Own DNS")}
              >
                <h5>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill={dnsType === "Own DNS" ? "#008060" : "#6b7280"}>
                    <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
                  </svg>
                  Own DNS
                </h5>
                <p>Use your existing DNS provider. Add an A record pointing to our server IP.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button
            className={styles.cancelButton}
            onClick={() => setAddDomainModal(false)}
          >
            Cancel
          </button>
          <button
            className={styles.submitButton}
            onClick={submitDomain}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className={styles.spinner}></span>
                Adding...
              </>
            ) : (
              "Add Domain"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddDomainModal;
